import { Component, Input, Output, EventEmitter, ViewChildren, QueryList, ElementRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-otp-input',
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: OtpInputComponent,
      multi: true
    }
  ]
})
export class OtpInputComponent implements ControlValueAccessor, OnInit {
  @Input() length = 6;
  @Output() otpChange = new EventEmitter<string>();

  @ViewChildren('otpInput') otpInputs: QueryList<ElementRef<HTMLInputElement>>;

  inputs: string[] = [];
  private onChange: (value: string) => void;
  public onTouched: () => void;

  ngOnInit() {
    this.inputs = Array(this.length).fill('');
  }

  // TrackBy function to prevent DOM re-creation
  trackByFn(index: number): number {
    return index;
  }

  handleInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Ensure only the last character is taken if multiple are typed (though maxlength is 1)
    // This handles the case where a user might type fast or on mobile
    if (value.length > 1) {
      value = value.slice(-1);
      input.value = value;
    }

    // If it's not a number, clear it
    if (!/^[0-9]$/.test(value)) {
      input.value = '';
      return;
    }

    this.inputs[index] = value;
    this.updateFormValue();

    // Move to next input if value is entered
    if (value && index < this.length - 1) {
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
    }
  }

  handleKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    // Handle backspace
    if (event.key === 'Backspace') {
      if (!input.value && index > 0) {
        // If empty and backspace pressed, move to previous and clear it
        const prevInput = this.otpInputs.toArray()[index - 1].nativeElement;
        this.inputs[index - 1] = '';
        prevInput.value = '';
        prevInput.focus();
        this.updateFormValue();
      } else {
        // Just clear current
        this.inputs[index] = '';
        input.value = '';
        this.updateFormValue();
      }
      return;
    }

    // Handle Left Arrow
    if (event.key === 'ArrowLeft' && index > 0) {
      this.otpInputs.toArray()[index - 1].nativeElement.focus();
      return;
    }

    // Handle Right Arrow
    if (event.key === 'ArrowRight' && index < this.length - 1) {
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
      return;
    }
  }

  handlePaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = (event.clipboardData && event.clipboardData.getData('text')) || '';
    const digits = pasted.replace(/[^0-9]/g, '').slice(0, this.length).split('');

    const inputsArray = this.otpInputs.toArray();

    // Fill inputs starting from the first one (or focused one? usually first or current)
    // Standard behavior: fill from start or fill from current. Let's fill from start for simplicity and consistency
    // or better, fill from the current focused index? The user didn't specify, but filling from 0 is safer.
    // Let's stick to filling from 0 as per previous logic, but ensure we don't overflow.

    for (let i = 0; i < this.length; i++) {
      if (i < digits.length) {
        this.inputs[i] = digits[i];
        if (inputsArray[i]) inputsArray[i].nativeElement.value = digits[i];
      } else {
        // Optional: clear remaining? Or keep them? 
        // Usually paste replaces everything or fills empty. 
        // Let's keep existing behavior: overwrite from 0.
      }
    }

    this.updateFormValue();

    // Focus the last filled input or the next empty one
    const nextIndex = Math.min(digits.length, this.length - 1);
    if (inputsArray[nextIndex]) {
      inputsArray[nextIndex].nativeElement.focus();
    }
  }

  private updateFormValue() {
    const value = this.inputs.join('');
    this.otpChange.emit(value);
    if (this.onChange) {
      this.onChange(value);
    }
    if (this.onTouched) {
      this.onTouched();
    }
  }

  writeValue(value: string): void {
    if (!value) {
      this.inputs = Array(this.length).fill('');
      if (this.otpInputs) {
        this.otpInputs.forEach(inp => {
          if (inp && inp.nativeElement) {
            inp.nativeElement.value = '';
          }
        });
      }
      return;
    }

    const digits = value.replace(/[^0-9]/g, '').slice(0, this.length).split('');
    // Pad with empty strings if value is shorter than length
    while (digits.length < this.length) {
      digits.push('');
    }

    this.inputs = digits;

    // Update DOM if view children exist
    if (this.otpInputs) {
      const inputsArray = this.otpInputs.toArray();
      for (let i = 0; i < this.length; i++) {
        if (inputsArray[i] && inputsArray[i].nativeElement) {
          inputsArray[i].nativeElement.value = this.inputs[i];
        }
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.otpInputs) {
      this.otpInputs.forEach(input => {
        input.nativeElement.disabled = isDisabled;
      });
    }
  }
}
